import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, Edit, Trash2, FolderTree } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Categories() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', parent_id: null, active: true });

  const fetch = () => axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(() => {});
  useEffect(() => { fetch(); }, []);

  const parentCats = categories.filter(c => !c.parent_id);

  const handleSave = async () => {
    if (!form.name) { toast.error('Nombre requerido'); return; }
    try {
      if (editItem) {
        await axios.put(`${API}/categories/${editItem.id}`, form, { headers });
        toast.success('Categoría actualizada');
      } else {
        await axios.post(`${API}/categories`, form, { headers });
        toast.success('Categoría creada');
      }
      setDialogOpen(false);
      setEditItem(null);
      setForm({ name: '', parent_id: null, active: true });
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`¿Eliminar "${cat.name}"?`)) return;
    try {
      await axios.delete(`${API}/categories/${cat.id}`, { headers });
      toast.success('Categoría eliminada');
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const openEdit = (cat) => {
    setEditItem(cat);
    setForm({ name: cat.name, parent_id: cat.parent_id, active: cat.active });
    setDialogOpen(true);
  };

  const openNew = (parentId = null) => {
    setEditItem(null);
    setForm({ name: '', parent_id: parentId, active: true });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-display font-semibold">Categorías</h1>
        <Button onClick={() => openNew()} className="gap-2"><Plus className="h-4 w-4" /> Nueva Categoría</Button>
      </div>

      <div className="space-y-4">
        {parentCats.map(cat => {
          const subs = categories.filter(c => c.parent_id === cat.id);
          return (
            <Card key={cat.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FolderTree className="h-4 w-4 text-muted-foreground" />
                    <span className="font-display font-semibold">{cat.name}</span>
                    <Badge variant={cat.active ? 'secondary' : 'outline'}>{cat.active ? 'Activa' : 'Inactiva'}</Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openNew(cat.id)}>+ Sub</Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(cat)}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(cat)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                {subs.length > 0 && (
                  <div className="ml-6 space-y-1">
                    {subs.map(sub => (
                      <div key={sub.id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-secondary/30 transition-colors duration-150">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{sub.name}</span>
                          <Badge variant={sub.active ? 'secondary' : 'outline'} className="text-xs">{sub.active ? 'Activa' : 'Inactiva'}</Badge>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(sub)}><Edit className="h-3 w-3" /></Button>
                          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(sub)}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editItem ? 'Editar Categoría' : 'Nueva Categoría'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nombre</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
            {form.parent_id && <p className="text-sm text-muted-foreground">Subcategoría de: {parentCats.find(c => c.id === form.parent_id)?.name}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave}>{editItem ? 'Actualizar' : 'Crear'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
